import { useCallback } from "react";
import {
  QueryCache,
  MutationCache,
  QueryClient,
  useQueryClient,
} from "@tanstack/react-query";
import { createRestomenuQueryClient } from "@restomenu/core/client";
import type { paths } from "@restomenu/core/types";
import type { PathsWithMethod } from "openapi-typescript-helpers";
import { API_URL } from "@/core/constants";
import { reportError } from "@/core/error-reporting";

// Колбэк подменяется бутстрапом сессии (core/session/bootstrap.ts); до его
// инициализации 401 просто молчит.
let onUnauthenticatedImpl: () => void = () => {};
export function setOnUnauthenticated(impl: () => void): void {
  onUnauthenticatedImpl = impl;
}

export const { api, fetchClient } = createRestomenuQueryClient({
  baseUrl: API_URL,
  onUnauthenticated: () => onUnauthenticatedImpl(),
});

export const queryClient = new QueryClient({
  // Замена onError-линка Apollo: тот был глобальным, и глобальным должен
  // остаться — иначе показ ошибки пришлось бы дублировать на каждом вызове.
  // Ключ передаём в reportError: по нему он отличает гостевые маршруты,
  // где 400 и 404 — состояние экрана, а не сбой (см. error-reporting.ts).
  queryCache: new QueryCache({
    onError: (error, query) => reportError(error, query.queryKey, query.meta),
  }),
  mutationCache: new MutationCache({ onError: (error) => reportError(error) }),
  defaultOptions: {
    queries: {
      // Обязательство Ф1: ретраи остаются транспорту (GET ×2 в fetch.ts),
      // иначе на лежачем бэке — до 9 запросов на один экранный.
      retry: false,
      staleTime: 30_000,
    },
    mutations: { retry: false },
  },
});

type GetPath = PathsWithMethod<paths, "get">;

/**
 * Конвенция инвалидации: ключи openapi-react-query — [method, path] /
 * [method, path, init], префикс ["get", path] накрывает все
 * параметризованные варианты.
 */
export function useInvalidate() {
  const qc = useQueryClient();
  // Стабильная идентичность: от возврата зависят useCallback/useMemo
  // потребителей — новая функция на каждый рендер пересоздавала бы их все.
  return useCallback(
    (invalidates: readonly GetPath[]) => {
      for (const p of invalidates) {
        void qc.invalidateQueries({ queryKey: ["get", p] });
      }
    },
    [qc],
  );
}
